import { useCallback, useMemo, useState } from "react";
import {
  KeepThemedCardValues,
  ReportThemedCardConfig,
  ReportValueCardProps,
} from "helpers/types";
import { SingleValueCardType } from "constants/index";
import {
  KEEP_TOTAL_OPTION,
  StrategyReportThemedCardDescriptions,
  StrategyReportThemedCardLabels,
  StrategyReportThemedCardValuesLabels,
} from "helpers/constants";
import { useStrategyReportValue } from "hooks/useReportStorage";
import { STRATEGY_REPORT_DEFAULTS } from "./strategyReportDefaults";
import {
  KeepFiguresState,
  KeepRowKey,
  NO_KEEP_ROWS_EDITED,
  keepParticipationFigure,
  keepRowFigure,
} from "./keepFigures";
import HeaderAssetSelect from "./HeaderAssetSelect";

/**
 * The Keep card: its eight rows, the asset picker in its header, and which of
 * KeepFiguresState's three states its figures are in.
 *
 * The response carries one entry per Bucket 1 asset plus their total, and
 * the option picked in the header select drives every field of the card.
 */
export const useKeepCard = () => {
  const [sharesStart, setSharesStart] = useStrategyReportValue(
    "shares_start_strategy",
    STRATEGY_REPORT_DEFAULTS.SHARES_START,
  );
  const [sharesEnd, setSharesEnd] = useStrategyReportValue(
    "shares_end_strategy",
    STRATEGY_REPORT_DEFAULTS.SHARES_END,
  );
  const [valueStart, setValueStart] = useStrategyReportValue(
    "value_start_strategy",
    STRATEGY_REPORT_DEFAULTS.VALUE_START,
  );
  const [valueEnd, setValueEnd] = useStrategyReportValue(
    "value_end_strategy",
    STRATEGY_REPORT_DEFAULTS.VALUE_END,
  );
  const [dividends, setDividends] = useStrategyReportValue(
    "dividends_strategy",
    STRATEGY_REPORT_DEFAULTS.DIVIDENDS,
  );
  // A CALCULATED row as of task 3345, so it stores the way its seven
  // neighbours on this card do rather than through useUncalculatedReportValue.
  // That hook's own docstring draws the line: its report-scoped holder exists
  // for a row NO run can overwrite, where a figure typed for client A would
  // otherwise still be on screen under client B's name. A row a run replaces
  // is survivable in the flat store -- the next run overwrites it, and until
  // then keepRowFigure's card state is what stops the previous run's number
  // reading as this one's. Its old `upside_participation_strategy` key is
  // retired with it; whatever a browser holds under it is never read again.
  const [participationRate, setParticipationRate] = useStrategyReportValue(
    "participation_rate_strategy",
    STRATEGY_REPORT_DEFAULTS.PARTICIPATION_RATE,
  );
  // Why the rate has none, when it has none -- the picked option's own
  // sentence, in component state rather than storage: the reason belongs to
  // the RUN while the figure it explains is persisted, so a reload must not
  // resurrect a refusal from a run that is gone. Set only in applyKeepShares,
  // beside the figure it qualifies, so the pair can never come from two
  // different options.
  const [participationUnavailable, setParticipationUnavailable] = useState<
    string | undefined
  >(undefined);
  const [basis, setBasis] = useStrategyReportValue(
    "basis_strategy",
    STRATEGY_REPORT_DEFAULTS.BASIS,
  );
  const [taxSavings, setTaxSavings] = useStrategyReportValue(
    "tax_savings_strategy",
    STRATEGY_REPORT_DEFAULTS.TAX_SAVINGS,
  );

  const [keepShares, setKeepShares] = useState<KeepThemedCardValues | null>(
    null,
  );
  const [selectedKeepAsset, setSelectedKeepAsset] = useState(KEEP_TOTAL_OPTION);
  // Which of the three states the card's figures are in, seeded at "unrun"
  // because on a fresh mount that is exactly what is true: nothing has run in
  // this session, so the persisted figures on the rows belong to whatever was
  // last on screen and none of them is this period's result.
  //
  // Component state, never written through to storage: it is a fact about
  // THIS session's run, and persisting it would outlive the run it describes.
  const [keepFiguresState, setKeepFiguresState] =
    useState<KeepFiguresState>("unrun");
  // The rows the reader has typed into since the card's figures last changed.
  // keepRowFigure carries why this is per row rather than per card.
  const [keepRowsEdited, setKeepRowsEdited] =
    useState<ReadonlySet<KeepRowKey>>(NO_KEEP_ROWS_EDITED);

  const applyKeepShares = useCallback(
    (card: KeepThemedCardValues, option: string) => {
      const values =
        option === KEEP_TOTAL_OPTION ? card.total : card.assets[option];
      // Every row now carries THIS option's figure, so nobody's earlier edit
      // is on screen any more and none may keep claiming a row. It matters
      // most on an asset SWITCH: a row still marked edited would show the
      // figure set just below without the reason that came with it, so an
      // asset whose rate the backend refused would read as a silent blank
      // instead of an explained N/A.
      setKeepRowsEdited(NO_KEEP_ROWS_EDITED);
      setSharesStart(values.shares_start);
      setSharesEnd(values.shares_end);
      setValueStart(values.value_start);
      setValueEnd(values.value_end);
      setDividends(values.dividends);
      setBasis(values.basis);
      setTaxSavings(values.tax_savings);
      // The Participation Rate and the sentence saying why it has none,
      // applied TOGETHER and from this ONE option -- which is what makes them
      // a pair the reader can trust, and why they are read out of one call.
      // keepParticipationFigure carries the rest.
      const participation = keepParticipationFigure(values);
      setParticipationRate(participation.rate);
      setParticipationUnavailable(participation.reason);
    },
    [
      setSharesStart,
      setSharesEnd,
      setValueStart,
      setValueEnd,
      setDividends,
      setBasis,
      setTaxSavings,
      setParticipationRate,
    ],
  );

  // Applies a completed run's Keep payload, and returns the card it adopted
  // (null when the run carried no Bucket 1 data) -- the Diversify card's stock
  // switch is built from the same assets.
  const applyRun = useCallback(
    (keep: KeepThemedCardValues | undefined): KeepThemedCardValues | null => {
      const card = keep?.assets ? keep : null;
      setKeepShares(card);
      // WHICH of the two answers this run gave about Bucket 1, and the reason
      // it is recorded rather than inferred from `keepShares` at the row.
      // This is the ONE transition that can take the card out of "unrun", so
      // it is also the only place that can tell "a run carried no Bucket 1
      // data" apart from "no run has happened yet" — and those two get
      // different words on the rows (KeepFiguresState says which and why).
      setKeepFiguresState(card ? "figures" : "absent");
      if (card) {
        // Keep the previously picked option when the new results still
        // carry it; otherwise fall back to the total.
        const option = card.assets[selectedKeepAsset]
          ? selectedKeepAsset
          : KEEP_TOTAL_OPTION;
        setSelectedKeepAsset(option);
        applyKeepShares(card, option);
      } else {
        // A run that carried no Bucket 1 data leaves every Keep row reading
        // N/A behind KEEP_NO_BUCKET_1, which the card state set just above is
        // what does (task 3155). What has to happen here is the reader's own
        // edits being dropped: they belong to the run this one has just
        // replaced.
        setKeepRowsEdited(NO_KEEP_ROWS_EDITED);
      }
      return card;
    },
    [selectedKeepAsset, applyKeepShares],
  );

  // A change of report inputs retires the run on screen. The figures go with
  // the picker, and they have to: the picker is the only thing on the card
  // that says WHICH stock its numbers are, so figures left standing without
  // it are the previous account's under this one's name. Back to "unrun",
  // which is exactly what is true again — no run has produced a Keep card for
  // these inputs — and the reader's own edits go with them, because they were
  // typed against the run this change of inputs has just retired.
  const resetForNewInputs = useCallback(() => {
    setKeepShares(null);
    setKeepFiguresState("unrun");
    setKeepRowsEdited(NO_KEEP_ROWS_EDITED);
  }, []);

  // A Keep row's setter with the reader's mark in front of it, so every row
  // of the card takes an edit the same way: the binding is what stops one
  // row's handler being written differently from the next.
  //
  // Typing into a row is the reader supplying the figure this run did not, so
  // THAT ROW ALONE stops reading its seed or its N/A and shows what they
  // typed. Only the mark is held here -- the VALUE persists through the row's
  // own storage hook, exactly as it always did.
  const keepRowChange = useCallback(
    (key: KeepRowKey, setValue: (value: number | string) => void) =>
      (value: number | string) => {
        setKeepRowsEdited((edited) =>
          edited.has(key) ? edited : new Set(edited).add(key),
        );
        setValue(value);
      },
    [],
  );

  const handleKeepAssetChange = useCallback(
    (option: string) => {
      setSelectedKeepAsset(option);
      if (keepShares) applyKeepShares(keepShares, option);
    },
    [keepShares, applyKeepShares],
  );

  // EVERY row reads its figure through keepRowFigure rather than off its
  // storage hook directly, and that is the whole of task 3155's fix: a stored
  // figure is only this run's result while the card is in the "figures"
  // state, and a row that renders one at any other time is asserting that
  // this run produced it. The stored value is never overwritten to achieve
  // that — it waits underneath, so an operator's hand-entered number survives
  // a run that could not replace it.
  const rows = useMemo((): ReportValueCardProps[] => {
    // One row of the card: its figure through keepRowFigure, its edit
    // through keepRowChange. Only Participation Rate passes a reason.
    const row = (
      key: KeepRowKey,
      label: string,
      type: SingleValueCardType,
      stored: number | string,
      setValue: (value: number | string) => void,
      reason?: string,
    ): ReportValueCardProps => ({
      label,
      ...keepRowFigure(
        keepFiguresState,
        keepRowsEdited.has(key),
        stored,
        reason,
      ),
      type,
      onValueChange: keepRowChange(key, setValue),
    });
    const labels = StrategyReportThemedCardValuesLabels;
    const { NUMBER, CURRENCY, PERCENTAGE } = SingleValueCardType;
    return [
      row(
        "shares_start",
        labels.SHARES_START,
        NUMBER,
        sharesStart,
        setSharesStart,
      ),
      row("shares_end", labels.SHARES_END, NUMBER, sharesEnd, setSharesEnd),
      row(
        "value_start",
        labels.VALUE_START,
        CURRENCY,
        valueStart,
        setValueStart,
      ),
      row("value_end", labels.VALUE_END, CURRENCY, valueEnd, setValueEnd),
      row("dividends", labels.DIVIDENDS, CURRENCY, dividends, setDividends),
      // The one row of this card that carries a reason of its own, so it is
      // the one that passes keepRowFigure's fourth argument. Everything else
      // about it is the card's ordinary machinery: "figures" pairs the stored
      // rate with the sentence, "absent"/"unrun" outrank both, and an edit
      // outranks all of them (keepRowFigure says why for each).
      row(
        "participation_rate",
        labels.PARTICIPATION_RATE,
        PERCENTAGE,
        participationRate,
        setParticipationRate,
        participationUnavailable,
      ),
      row("basis", labels.BASIS, CURRENCY, basis, setBasis),
      row(
        "tax_savings",
        labels.TAX_SAVINGS,
        CURRENCY,
        taxSavings,
        setTaxSavings,
      ),
    ];
  }, [
    keepFiguresState,
    keepRowsEdited,
    keepRowChange,
    sharesStart,
    sharesEnd,
    valueStart,
    valueEnd,
    dividends,
    participationRate,
    participationUnavailable,
    basis,
    taxSavings,
    setSharesStart,
    setSharesEnd,
    setValueStart,
    setValueEnd,
    setDividends,
    setParticipationRate,
    setBasis,
    setTaxSavings,
  ]);

  const card = useMemo(
    (): ReportThemedCardConfig => ({
      label: StrategyReportThemedCardLabels.KEEP,
      description: StrategyReportThemedCardDescriptions.KEEP,
      values: rows,
      addictional_component: null,
      headerAction: keepShares ? (
        <HeaderAssetSelect
          value={selectedKeepAsset}
          options={[KEEP_TOTAL_OPTION, ...Object.keys(keepShares.assets)]}
          onChange={handleKeepAssetChange}
        />
      ) : undefined,
    }),
    [rows, keepShares, selectedKeepAsset, handleKeepAssetChange],
  );

  return { card, keepShares, keepFiguresState, applyRun, resetForNewInputs };
};
